/**
 * Pure helpers and config for InterpreterQueueService.
 */
import { getUserRole } from '../base/user-role/functions';
import { APP_TYPE } from '../base/whitelabel/constants';
import { getAppType, getWhitelabelConfig } from '../base/whitelabel/functions';
import { getSecureItem } from '../vrs-auth/secureStorage';
import { getPersistentJson, getPersistentItem } from '../vrs-auth/storage';
import { TOKEN_EXPIRY_SKEW_MS, isAccessTokenExpired } from '../vrs-auth/accessTokenExpiry';
import { getHandoffDeviceId } from './browserDeviceId';
const CONNECTION_ERROR_LOG_INTERVAL = 60000;
const SEND_WARNING_LOG_INTERVAL = 30000;
const PENDING_AUTHED_MESSAGE_LIMIT = 10;
// How long a queue WebSocket must stay open before the reconnect backoff
// counter resets. Captive portals / IPS middleboxes complete the TLS and WS
// handshake and then abort the flow in under a second; resetting the counter
// on open would turn exponential backoff into a ~2s forever-loop.
const RECONNECT_STABILITY_WINDOW_MS = 30000;
function normalizeP2PPhoneTarget(target) {
    const cleaned = String(target || '').replace(/[^\d+]/g, '');
    return cleaned.length >= 7 && cleaned.length <= 16 ? cleaned : null;
}
function getErrorMessage(error, fallback) {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    const maybeError = error;
    if (typeof maybeError?.message === 'string' && maybeError.message) {
        return maybeError.message;
    }
    if (typeof maybeError?.type === 'string' && maybeError.type) {
        return maybeError.type;
    }
    return fallback;
}
function getTenantQueueDomain(role) {
    const domains = getWhitelabelConfig()?.domains;
    if (!domains) {
        return undefined;
    }
    if (role === 'interpreter') {
        return domains.queue || domains.interpreter || domains.api || domains.clientVri || domains.clientVrs;
    }
    if (getAppType() === APP_TYPE.VRI) {
        return domains.queue || domains.api || domains.clientVri || domains.clientVrs || domains.interpreter;
    }
    return domains.queue || domains.api || domains.clientVrs || domains.clientVri || domains.interpreter;
}
function getDefaultQueueServiceUrl() {
    if (typeof window !== 'undefined' && window.location?.host) {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsProtocol}//${window.location.host}/ws`;
    }
    const domain = getTenantQueueDomain(getUserRole());
    if (domain) {
        return `wss://${domain}/ws`;
    }
    return 'wss://vrs.malkacomm.com/ws';
}
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function stringValue(value) {
    return typeof value === 'string' ? value : undefined;
}
function numberValue(value) {
    return typeof value === 'number' ? value : undefined;
}
function callTypeValue(value) {
    return value === 'vrs' || value === 'vri' ? value : undefined;
}
function normalizeInterpreterStatus(value) {
    return value === 'busy' || value === 'inactive' ? value : 'active';
}
function recordPayload(data) {
    return isRecord(data) ? data : {};
}
function getVRSConfig() {
    const defaults = {
        queueServiceUrl: getDefaultQueueServiceUrl(),
        queue: {
            maxWaitTime: 10,
            estimatedWaitPerPerson: 2
        }
    };
    if (typeof config !== 'undefined' && config?.vrs) {
        return {
            ...defaults,
            ...config.vrs,
            queue: {
                ...defaults.queue,
                ...config.vrs.queue
            }
        };
    }
    return defaults;
}
function getStoredJson(key) {
    return getPersistentJson(key);
}
function getStoredAuthToken() {
    const raw = getSecureItem('vrs_auth_token') || getPersistentItem('vrs_auth_token');
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed;
    }
    catch {
        return { token: raw };
    }
}
function getCurrentRoomName() {
    const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
    if (!pathname) {
        return undefined;
    }
    const path = pathname.split('/').filter(Boolean);
    const roomName = path[path.length - 1];
    if (!roomName || roomName.endsWith('.html')) {
        return undefined;
    }
    return roomName;
}
function hasQueueAuthForRole(role) {
    if (role === 'captioner') {
        return false;
    }
    const storedToken = getStoredAuthToken();
    return Boolean(storedToken?.token);
}
function getPresenceHeartbeatPayload() {
    const documentRef = typeof document !== 'undefined' ? document : undefined;
    const windowRef = typeof window !== 'undefined' ? window : undefined;
    const navigatorRef = typeof navigator !== 'undefined' ? navigator : undefined;
    return {
        deviceType: navigatorRef?.product === 'ReactNative' ? 'native' : 'web',
        focused: typeof documentRef?.hasFocus === 'function' ? documentRef.hasFocus() : undefined,
        path: windowRef?.location?.pathname,
        visibility: documentRef?.visibilityState
    };
}
export { normalizeP2PPhoneTarget, getErrorMessage, getTenantQueueDomain, getDefaultQueueServiceUrl, isRecord, stringValue, numberValue, callTypeValue, normalizeInterpreterStatus, recordPayload, getVRSConfig, getStoredJson, getStoredAuthToken, isAccessTokenExpired, getCurrentRoomName, hasQueueAuthForRole, getPresenceHeartbeatPayload, getHandoffDeviceId, CONNECTION_ERROR_LOG_INTERVAL, RECONNECT_STABILITY_WINDOW_MS, SEND_WARNING_LOG_INTERVAL, PENDING_AUTHED_MESSAGE_LIMIT, TOKEN_EXPIRY_SKEW_MS };
